/**
 * Agent chat page - main entry point for the AI agent interface.
 * Uses split-pane layout with message history and control panel.
 */
import type { ChatMessage } from "@nucleus/shared-types";
import { useState } from "react";

import { ChatLayout } from "./ChatLayout";
import { MessageInput } from "./components/MessageInput";
import { MessageList } from "./components/MessageList";

/**
 * Message area component with message list and input
 */
function MessageArea() {
	// Temporary test state - will be replaced with proper chat state management
	const [messages, setMessages] = useState<ChatMessage[]>([]);

	/**
	 * Handles message submission
	 * @param message - The message content to send
	 */
	const handleSubmit = (message: string) => {
		// TODO: Integrate with chat state management and API (TASK-011)
		const newMessage: ChatMessage = {
			id: `msg-${Date.now()}`,
			role: "user",
			content: message,
			createdAt: new Date(),
			status: "complete",
		};
		setMessages((prev) => [...prev, newMessage]);

		// Simulate assistant response with markdown after a delay
		setTimeout(() => {
			const assistantMessage: ChatMessage = {
				id: `msg-${Date.now()}`,
				role: "assistant",
				content: `# Response to your message

Here's what you said:
> ${message}

## Features demonstrated:
- **Markdown** formatting works
- \`inline code\` rendering
- Lists and tables

\`\`\`typescript
// Code blocks with syntax highlighting
const greeting = "Hello, World!";
console.log(greeting);
\`\`\`

| Feature | Status |
|---------|--------|
| Markdown | ✓ |
| Code blocks | ✓ |
| Tables | ✓ |
`,
				createdAt: new Date(),
				status: "complete",
				model: "Claude Sonnet 4.5",
			};
			setMessages((prev) => [...prev, assistantMessage]);
		}, 1000);
	};

	return (
		<div className="flex h-full flex-col">
			{/* Message list area */}
			<div className="flex-1 overflow-hidden">
				<MessageList messages={messages} />
			</div>

			{/* Input area - sticky at bottom */}
			<div className="sticky bottom-0 border-t bg-background p-4">
				<MessageInput onSubmit={handleSubmit} />
			</div>
		</div>
	);
}

/**
 * Placeholder control panel component
 */
function ControlPanel() {
	return (
		<div className="flex h-full flex-col p-4">
			<div className="rounded-lg border border-dashed p-4">
				<h3 className="text-sm font-medium text-muted-foreground">Controls</h3>
				<p className="mt-2 text-sm text-muted-foreground">
					Model selection, settings, and file management will be configured here.
				</p>
			</div>
		</div>
	);
}

/**
 * Agent page component
 */
export function AgentPage() {
	return (
		<div className="h-full">
			<ChatLayout
				messageArea={<MessageArea />}
				controlPanel={<ControlPanel />}
			/>
		</div>
	);
}
